import React, { PropsWithChildren } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import { AccordionV2 } from './accordion-v2';
import { Heading } from '@restworld/ui-ds-base';
import IconButton, { IconButtonProps, IconComponentType } from '../icon-button';

import { cn } from '@restworld/utils-common';
export interface SectionAccordionProps {
  open?: boolean;
  title: string;
  toggleBtn?: {
    Icon?: IconComponentType;
    wrapperStyle?: string;
  };
  headerRef?: React.RefObject<HTMLDivElement | null>;
  iconButton?: IconButtonProps;
  onClickIconButton?: () => void;
  TitleIcon?: IconComponentType;
  titleStyle?: string;
}

export function SectionAccordion({
  children,
  toggleBtn,
  headerRef,
  iconButton,
  title,
  onClickIconButton,
  TitleIcon,
  titleStyle,
  open = true,
}: PropsWithChildren<SectionAccordionProps>) {
  return (
    <AccordionV2 className="bg-transparent rounded-none" open={open}>
      <HeaderWrapper twStyle={toggleBtn?.wrapperStyle} ref={headerRef}>
        <TitleAndButtonWrapper>
          <div className="flex items-center gap-1">
            {!!TitleIcon && <TitleIcon className="h-6 w-6 min-h-[24px] min-w-[24px]" />}
            <div className="flex">
              <Heading
                as="span"
                size={5}
                weight="semibold"
                color="neutral"
                className={cn('leading-normal min-w-fit whitespace-nowrap text-lg md:text-xl', titleStyle)}
              >
                {title}
              </Heading>
            </div>
          </div>
          {iconButton?.Icon && (
            <IconButton Icon={iconButton.Icon} ariaLabel={iconButton?.ariaLabel || ''} onClick={onClickIconButton} />
          )}
        </TitleAndButtonWrapper>
        {/* The toggle renders no children, so without a name it reaches screen
            readers as an unnamed button. The section title is the natural name
            and every caller already provides it. */}
        <AccordionV2.Button
          className="bg-transparent text-white w-fit rounded-none"
          IconComponent={toggleBtn?.Icon || ChevronDownIcon}
          iconAriaLabel={title}
          wrapperStyle={'flex justify-end items-center'}
        />
      </HeaderWrapper>

      <AccordionV2.Panel className="py-0">{children}</AccordionV2.Panel>
    </AccordionV2>
  );
}

export default SectionAccordion;

const HeaderWrapper = React.forwardRef<HTMLDivElement, { twStyle?: string } & React.HTMLAttributes<HTMLDivElement>>(
  ({ twStyle, className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center justify-between pb-6', twStyle as string, className)} {...props} />
  )
);
HeaderWrapper.displayName = 'HeaderWrapper';
export const TitleAndButtonWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-center gap-3', className)} {...props} />
);
